import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { find } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { unsubscribeComponent } from 'Src/ng2/shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { CellDisplayService } from 'Src/ng2/shared/services/list-services/cell-display.service';
import { IFocus } from '../../../../school/tiles/interfaces/dashboard.interfaces';
import { DashboardCircleService } from '../../../../shared/services/dashboard-circle-service/dashboard-circle-service';
import { HeaderService } from '../../../../shared/services/list-services/header.service';
import { RollupGroupingFooterService } from '../../../../shared/services/rollup-grouping-footer-service/rollup-grouping-footer-service';
import { SortAndFilterService } from '../../../services/list-services/sort-and-filter.service';
import { IColumn, IGroupData, IRowData } from './../../../models/list-models';

interface IColumnIndexMap {
  [key: string]: number;
}

// this component should be cleaned up before tests are written - JJ
/* istanbul ignore next */
@Component({
  selector: 'rollup-table',
  template: require('./rollup-table.component.html'),
  styles: [require('./rollup-table.component.scss')],
})
@unsubscribeComponent
export class RollupTableComponent implements OnInit {
  @Input() currentSort;

  @Output() hoveredTableRow = new EventEmitter<IRowData>();
  @Output() clickedTableRow = new EventEmitter<IRowData>();
  @Input() focusData$: Observable<IFocus>;
  @Input() rollupGrouping$: Observable<IGroupData>;
  @Input() columns: IColumn[];
  @Input() hoveredBar: number | string;

  groupData: IGroupData;
  footerRow$ = new BehaviorSubject<IRowData[]>([]);
  updateSort: Function;
  sortRowData: Function;
  sortKey$ = new BehaviorSubject<string>('STUB');
  sortDirection$ = new BehaviorSubject<string>('des');
  dataSource$ = new BehaviorSubject<IRowData[][]>([]);
  rowCount: number;
  groupingName: string;
  dataSourceSub: Subscription;
  focusDataSub: Subscription;
  getHeaderOverflow: Function = HeaderService.getHeaderOverflow;
  headerOverflow: boolean = false;

  constructor(
    private rollupGroupingFooterService: RollupGroupingFooterService,
    private dashboardCircleService: DashboardCircleService,
  ) {}

  ngOnInit(): void {
    this.updateSort = SortAndFilterService.updateSortCol;
    this.sortRowData = SortAndFilterService.sortRollupTableRowData;

    const midLevelColumns$ = this.focusData$.pipe(
      switchMap(focusData => {
        const { midLevelColumns } = focusData;
        const defaultSortKey = find(midLevelColumns, 'isFooterSetter').graphQlKey;
        this.sortKey$.next(defaultSortKey);
        this.sortDirection$.next('des');
        return [midLevelColumns];
      }),
    );

    const rowData$ = this.rollupGrouping$.pipe(
      switchMap(rollupGrouping => {
        // parse out row data and footer row data
        const combinedRowData = rollupGrouping.rowData.reduce(
          (accum, row: IRowData[]) => {
            const totalRow = find(row, { data: 'All' });
            if (totalRow) {
              accum.total = row;
            } else {
              accum.data.push(row);
            }
            return accum;
          },
          { total: [], data: [] },
        );
        const rowData = combinedRowData.data ? combinedRowData.data : [];
        const sortKeyIndex = 1; // initial sort is always second column.
        const sortedRowData = this.sortRowData(rowData, sortKeyIndex, 'des');
        const colors = this.dashboardCircleService.getColors(sortedRowData);
        const rowDataWithColors = sortedRowData.reduce((accum, row: IRowData[], idx: number) => {
          const [stubRow, ...others] = row;
          const rowWithColor = { ...stubRow, meta: Object.assign(JSON.parse(stubRow.meta), { color: colors[idx] }) };
          accum.push([rowWithColor, ...others]);
          return accum;
        }, []);
        // Parse footer row's meta data as well - jchu
        const [stubRowTotal, ...others] = combinedRowData.total;
        const totalRow = [{ ...stubRowTotal, meta: Object.assign({}, JSON.parse(stubRowTotal.meta)) }, ...others];
        return [{ ...rollupGrouping, rowData: rowDataWithColors, totalRow }];
      }),
    );

    this.dataSourceSub = combineLatest(rowData$, this.sortKey$, this.sortDirection$, midLevelColumns$)
      .pipe(
        tap(([groupingData, sortKey, sortDirection, midLevelColumns]) => {
          const columnIndexMap = this.getColumnIndexMap(midLevelColumns);
          this.groupData = groupingData;
          const footerRow = groupingData.totalRow;
          const rowData = this.groupData.rowData;
          this.rowCount = rowData.length;
          const index = columnIndexMap[sortKey];
          const sortKeyIndex = index || index === 0 ? index : 1;
          const sortedRowData = this.sortRowData(rowData, sortKeyIndex, sortDirection);
          const formattedRowData = CellDisplayService.getFormattedRowData({
            sortedRowData,
            columns: this.columns,
            listType: 'ROLLUP',
          });
          const formattedFooterRow = this.rollupGroupingFooterService.getFormattedFooterRow({
            footerRow,
            columns: this.columns,
          });
          this.footerRow$.next(formattedFooterRow);
          this.dataSource$.next(formattedRowData);
        }),
      )
      .subscribe();

    // TODO: add selected group to rollup grouping so it can be pulled off.
    // otherwise emit up to the parent from the madlib model and down to the child.
    this.focusDataSub = combineLatest(this.focusData$, this.rollupGrouping$)
      .pipe(
        tap(([focusData, groupingData]) => {
          const { midLevelGroupingOptions } = focusData;
          const groupingKey = groupingData.key;
          [...midLevelGroupingOptions].forEach((group: any) => {
            if (group.graphQlKey === groupingKey && !group.children) {
              this.groupingName = group.parent;
            } else {
              if (group.children && group.children.length > 0) {
                group.children.forEach((childGroup: any) => {
                  if (childGroup.graphQlKey === groupingKey) this.groupingName = childGroup.child;
                });
              }
            }
          });
        }),
      )
      .subscribe();
  }

  getColumnIndexMap(columns: IFocus['midLevelColumns']): IColumnIndexMap {
    const columnIndexMap = columns.reduce(
      (mapping, col: IColumn, i: number) => {
        mapping[col.graphQlKey] = i + 1;
        return mapping;
      },
      { STUB: 0 },
    );
    return columnIndexMap;
  }

  emitHoveredRow(row: IRowData): void {
    this.hoveredTableRow.emit(row);
  }

  emitClickedRow(row: IRowData): void {
    this.clickedTableRow.emit(row);
  }

  checkHeaderOverflow(className: string): void {
    this.headerOverflow = this.getHeaderOverflow(className);
  }

  resetHeaderOverflow(): void {
    this.headerOverflow = false;
  }
}
